import axios from 'axios';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import { injectable } from 'inversify';
import { ServiceTypes } from 'constants/ServiceTypes';
import { container } from 'services/ioc/ioc';
import { LogService } from './LogService';
import { auth } from '../stores/AuthorizationStore';
import { ConfigurationService } from './ConfigurationService';

interface TokenResponse {
  isAuthenticated: boolean;
  accessToken: string;
  expires_at: string;
  id_token: string;
}

@injectable()
export class AuthenticationService {
  private configService: ConfigurationService;

  private pathAccountMe = 'account/me';

  private pathAccountLogout = 'account/logout';

  private pathAccountPermissions = 'account/privileges';

  private isLoggedInStorageKey = 'isLoggedIn';

  private isLoggedIn = true;

  private authData: TokenResponse | null = null;

  private logService: LogService;

  constructor() {
    this.configService = new ConfigurationService();
    this.logService = container.get<LogService>(ServiceTypes.LogService);
  }

  initialize = async () => {
    await this.getToken();
    await this.getPermissions();

    window.addEventListener('storage', this.onStorageChanged);
  };

  getAuthData() {
    return this.authData;
  }

  getToken = async () => {
    if (!this.isLoggedIn) {
      // if we were already logged out once, don't allow to log in again in the same instance.
      // User needs to reload page to log in again.
      return;
    }

    try {
      const selfUrl = this.configService !== null && this.configService.getWindowOrigin();
      const requestURL = `${selfUrl}/${this.pathAccountMe}`;
      const response = await axios.get(requestURL, {
        maxRedirects: 0,
        withCredentials: true,
      });
      if (response.status === 400) {
        window.location.reload();
      }
      if (response.request.responseURL !== requestURL) {
        // Since this endpoint redirects to login page when unauthorized and we cannot prevent redirection in browser JS,
        // we detect it by comparing URL of request and response.

        // Redirected means unauthorized.
        this.setAuthInfo(null);
        return;
      }

      // Authorized.
      this.setAuthInfo(response.data);
    } catch (error) {
      this.logService.logError({ error, message: `Exception when getting token` });
      this.setAuthInfo(null);
    }
  };

  hasToken = (): boolean => {
    return (
      this.authData !== null &&
      Date.parse(this.authData.expires_at) - Date.now() > 1000 &&
      window.localStorage.getItem(this.isLoggedInStorageKey) !== null &&
      window.localStorage.getItem(this.isLoggedInStorageKey) !== 'false'
    );
  };

  setAuthInfo = (authInfo: TokenResponse | null) => {
    this.setIsLoggedIn(authInfo !== null);
    this.authData = authInfo;
  };

  getPermissions = async () => {
    const selfUrl = this.configService.getWindowOrigin();
    try {
      const response = await axios.get(`${selfUrl}/${this.pathAccountPermissions}`, { withCredentials: true, timeout: 5000 });
      if (response.status === 200) {
        ApplicationUserRights.setData(response.data);
      }
    } catch (e) {
      this.logService.logError({ error: e, message: `Exception when getting permissions` });
      console.log(e);
      window.location.reload();
    }
  };

  private onStorageChanged = (e: StorageEvent) => {
    if (e.key !== this.isLoggedInStorageKey) {
      return;
    }

    // refresh value when storage flag changes
    this.getToken();
  };

  private setIsLoggedIn(value: boolean) {
    if (value) {
      window.localStorage.setItem(this.isLoggedInStorageKey, 'true');
    } else {
      window.localStorage.removeItem(this.isLoggedInStorageKey);
    }

    this.isLoggedIn = value;
    auth.setIsLoggedIn(value);
  }
}
